/**
 * QuotationEditSessionModal — Controlled Mutation Layer (UI).
 *
 * The architectural contract this UI honours:
 *   1. Opens an edit session on mount (POST /edit-session) — the
 *      server returns a base_snapshot. We never touch the live
 *      quotation row.
 *   2. Every field change fires POST /edit-sessions/{id}/patch with
 *      the {scope, field, item_index?, new} delta. Patches are debounced
 *      per-field so typing doesn't flood the server.
 *   3. The right pane shows a live BEFORE / AFTER diff sourced from
 *      the server's working_state — single source of truth.
 *   4. "Apply Changes" calls /edit-sessions/{id}/apply which forks a
 *      new revision via the existing revision engine. Cancel rolls
 *      back the session.
 *
 * No optimistic UI: we always render the server's working_state. A
 * field that fails server validation reverts naturally on the next
 * poll/patch round.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  X, Save, GitBranch, CheckCircle2, AlertCircle, Loader2, Pencil,
} from 'lucide-react';

const fmtSAR = (n) => `${Number(n || 0).toLocaleString()} SAR`;

// Two-second debounce per (scope:field:item_index) so the server
// isn't pelted on every keystroke. The latest value always wins.
const PATCH_DEBOUNCE_MS = 700;

export default function QuotationEditSessionModal({ quotation, onClose, onApplied }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [reason, setReason]   = useState('');
  const [draft, setDraft]     = useState(null);   // local typing buffer

  // Debounce table per "scope:field:idx" → timer id
  const timersRef = useRef({});

  // ── 1. Open the session on mount ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.post(
          `/quotations/${quotation.id}/edit-session`
        );
        if (cancelled) return;
        setSession(data);
        setDraft(JSON.parse(JSON.stringify(data.base_snapshot)));
      } catch (err) {
        if (cancelled) return;
        const det = err.response?.data?.detail;
        setError(
          typeof det === 'object'
            ? (det.error === 'chain_not_editable'
                ? `Cannot edit — chain is ${det.status}`
                : det.error)
            : (err.response?.status === 403
                ? 'Your role is not allowed to edit quotations'
                : 'Failed to open edit session')
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quotation.id]);

  // ── 2. Patch dispatcher (debounced) ──────────────────────────
  const sendPatch = useCallback(async (patch) => {
    if (!session) return;
    try {
      await axiosInstance.post(
        `/quotations/edit-sessions/${session.id}/patch`,
        patch
      );
      // Refresh working_state from server (source of truth)
      const { data: refreshed } = await axiosInstance.get(
        `/quotations/edit-sessions/${session.id}`
      );
      setSession(refreshed);
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 400) {
        toast.error(`Field not patchable: ${det?.field || 'unknown'}`);
      } else if (err.response?.status === 409) {
        toast.error('Session is no longer open');
      } else {
        toast.error('Failed to save change');
      }
    }
  }, [session]);

  const scheduleHeaderPatch = (field, newVal) => {
    const key = `header:${field}`;
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => {
      sendPatch({ scope: 'header', field, new: newVal });
    }, PATCH_DEBOUNCE_MS);
  };

  const scheduleItemPatch = (idx, field, newVal) => {
    const key = `item:${field}:${idx}`;
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => {
      sendPatch({ scope: 'item', field, item_index: idx, new: newVal });
    }, PATCH_DEBOUNCE_MS);
  };

  // ── 3. Mutators (update local draft + schedule server patch) ─
  const updateHeader = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    scheduleHeaderPatch(field, value);
  };

  const updateItem = (idx, field, value) => {
    const coerced = (field === 'quantity' || field === 'unit_price')
      ? Number(value || 0)
      : value;
    setDraft((d) => {
      const items = [...(d.items || [])];
      items[idx] = { ...items[idx], [field]: coerced };
      // Local total preview — server still authoritative.
      const q = Number(items[idx].quantity || 0);
      const p = Number(items[idx].unit_price || 0);
      items[idx].total = Math.round(q * p * 100) / 100;
      return { ...d, items };
    });
    scheduleItemPatch(idx, field, coerced);
  };

  // ── Add new item — backend auto-pads items[] when a patch references
  // an index beyond the current length, so we push a blank row locally
  // AND immediately schedule a no-op patch on each default field. This
  // guarantees the new row reaches the server even if the user clicks
  // Apply before filling any field in.
  const addNewItem = () => {
    let newIdx = -1;
    setDraft((d) => {
      const items = [...(d.items || [])];
      newIdx = items.length;
      items.push({
        description: '',
        quantity:    1,
        unit:        'ea',
        unit_price:  0,
        total:       0,
      });
      return { ...d, items };
    });
    // Defer until state has settled so the index is stable.
    setTimeout(() => {
      if (newIdx < 0) return;
      // Touch every patchable field once to materialise the item on the
      // server's working_state during apply.
      scheduleItemPatch(newIdx, 'description', '');
      scheduleItemPatch(newIdx, 'quantity',     1);
      scheduleItemPatch(newIdx, 'unit',         'ea');
      scheduleItemPatch(newIdx, 'unit_price',   0);
    }, 0);
  };

  // ── 4. Apply / Cancel ────────────────────────────────────────
  const handleApply = async () => {
    if (!session) return;
    // Flush pending debounced patches BEFORE apply, otherwise the
    // very last keystrokes could be missed by the no_changes guard.
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    setSaving(true);
    try {
      const { data } = await axiosInstance.post(
        `/quotations/edit-sessions/${session.id}/apply`,
        { revision_reason: reason || undefined },
      );
      toast.success(
        `Revision ${data.new_revision.quotation_no} created — ${fmtSAR(data.new_revision.total)}`
      );
      onApplied?.(data.new_revision);
      onClose();
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 400 && det?.error === 'no_changes') {
        toast.error('No changes to apply — make at least one edit');
      } else if (err.response?.status === 409) {
        toast.error(`Cannot apply: ${det?.error || 'session closed'}`);
      } else {
        toast.error('Failed to apply changes');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (session?.id) {
      try {
        await axiosInstance.post(`/quotations/edit-sessions/${session.id}/cancel`);
      } catch (_) { /* sticky cancel; ignore */ }
    }
    onClose();
  };

  // ── 5. Derived diff rows for the right pane ──────────────────
  const diffRows = (() => {
    if (!session?.working_state || !session?.base_snapshot) return [];
    const before = session.base_snapshot;
    const after  = session.working_state;
    const rows = [];
    ['subject', 'notes', 'terms', 'quotation_date', 'valid_date'].forEach((f) => {
      if (before[f] !== after[f]) {
        rows.push({ label: f, before: before[f] ?? '—', after: after[f] ?? '—' });
      }
    });
    const max = Math.max((before.items || []).length, (after.items || []).length);
    for (let i = 0; i < max; i++) {
      const b = (before.items || [])[i] || {};
      const a = (after.items || [])[i] || {};
      ['description', 'quantity', 'unit', 'unit_price', 'total'].forEach((f) => {
        if (b[f] !== a[f]) {
          rows.push({ label: `items[${i}].${f}`, before: b[f] ?? '—', after: a[f] ?? '—' });
        }
      });
    }
    return rows;
  })();

  const dirty = diffRows.length > 0;

  // ── 6. Render ────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div
        data-testid="quotation-edit-session-modal"
        className="modal-content max-w-6xl w-full max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header flex items-center justify-between border-b pb-3 mb-4">
          <div>
            <h3 className="modal-title flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" />
              Edit Session · {quotation.quotation_no}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Every change is staged as a patch. Apply forks a new revision (R+1). Original stays untouched.
            </p>
          </div>
          <button
            data-testid="close-edit-session-button"
            onClick={handleCancel}
            className="btn-icon"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Opening edit session…
          </div>
        ) : error ? (
          <div
            data-testid="edit-session-error"
            className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-start gap-2"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Could not start edit session</div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        ) : draft && session ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* ── Editor pane ─────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Subject</label>
                  <input
                    data-testid="edit-session-subject"
                    type="text"
                    value={draft.subject || ''}
                    onChange={(e) => updateHeader('subject', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Valid Until</label>
                  <input
                    data-testid="edit-session-valid-date"
                    type="date"
                    value={(draft.valid_date || '').slice(0, 10)}
                    onChange={(e) => updateHeader('valid_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="form-label mb-0">Line Items</div>
                  <button
                    type="button"
                    data-testid="edit-session-add-item"
                    onClick={addNewItem}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                    + إضافة بند / Add Item
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-left w-16">Unit</th>
                        <th className="px-3 py-2 text-right w-28">Unit Price</th>
                        <th className="px-3 py-2 text-right w-28">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(draft.items || []).map((it, idx) => (
                        <tr key={idx} data-testid={`edit-session-row-${idx}`}>
                          <td className="px-2 py-1.5">
                            <input
                              data-testid={`edit-session-description-${idx}`}
                              type="text"
                              value={it.description || ''}
                              onChange={(e) => updateItem(idx, 'description', e.target.value)}
                              className="w-full bg-transparent border-0 focus:ring-1 focus:ring-indigo-300 px-2 py-1 rounded"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              data-testid={`edit-session-quantity-${idx}`}
                              type="number"
                              step="0.01"
                              value={it.quantity ?? 0}
                              onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                              className="w-full text-right bg-transparent border-0 focus:ring-1 focus:ring-indigo-300 px-2 py-1 rounded"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              data-testid={`edit-session-unit-${idx}`}
                              type="text"
                              value={it.unit || 'ea'}
                              onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                              className="w-full bg-transparent border-0 focus:ring-1 focus:ring-indigo-300 px-2 py-1 rounded"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              data-testid={`edit-session-unit-price-${idx}`}
                              type="number"
                              step="0.01"
                              value={it.unit_price ?? 0}
                              onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                              className="w-full text-right bg-transparent border-0 focus:ring-1 focus:ring-indigo-300 px-2 py-1 rounded"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-700">
                            {fmtSAR(it.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Allowed edits: <strong>description, quantity, unit, unit_price</strong>.
                  Totals + VAT recompute server-side.
                </p>
              </div>

              <div>
                <label className="form-label">Revision reason (recorded in audit trail)</label>
                <input
                  data-testid="edit-session-reason"
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="form-input"
                  placeholder="e.g. price negotiation R1 → R2"
                />
              </div>
            </div>

            {/* ── Diff pane ───────────────────────────────────── */}
            <div className="space-y-3">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                <div className="text-xs font-semibold text-indigo-800 uppercase mb-2 flex items-center gap-1">
                  <GitBranch className="w-3.5 h-3.5" />
                  Working Totals (Server-Computed)
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-mono">{fmtSAR(session.working_state?.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>VAT ({session.working_state?.vat_rate}%)</span>
                    <span className="font-mono">{fmtSAR(session.working_state?.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-indigo-900 pt-1 border-t border-indigo-200">
                    <span>Total</span>
                    <span className="font-mono">{fmtSAR(session.working_state?.total)}</span>
                  </div>
                </div>
              </div>

              <div
                data-testid="edit-session-diff"
                className="rounded-lg border border-gray-200 overflow-hidden"
              >
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-700 border-b">
                  Diff · {diffRows.length} change{diffRows.length === 1 ? '' : 's'}
                </div>
                {diffRows.length === 0 ? (
                  <div className="p-4 text-sm text-gray-400 italic">
                    No changes yet. Edit a field to see the diff.
                  </div>
                ) : (
                  <div className="divide-y max-h-80 overflow-y-auto">
                    {diffRows.map((row, i) => (
                      <div
                        key={i}
                        data-testid={`edit-session-diff-row-${i}`}
                        className="px-3 py-2 text-xs"
                      >
                        <div className="font-mono text-gray-500 mb-1">{row.label}</div>
                        <div className="flex items-center gap-2">
                          <span className="line-through text-red-500 truncate max-w-[40%]">
                            {String(row.before)}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-emerald-700 font-medium truncate max-w-[40%]">
                            {String(row.after)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && draft && (
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <button
              data-testid="apply-edit-session-button"
              type="button"
              disabled={saving || !dirty}
              onClick={handleApply}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-md disabled:opacity-50"
              title={!dirty ? 'Make at least one change first' : ''}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Applying…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Apply Changes &amp; Create Revision
                </>
              )}
            </button>
            <button
              data-testid="cancel-edit-session-button"
              type="button"
              onClick={handleCancel}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" />
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
